<template>
    <div class="documents-tab p-3">
        <ValidationObserver ref="documentsForm">
            <b-alert show :variant="isWaitingMode ? 'info' : 'danger'" class="d-flex align-items-center mb-4">
                <i class="ri-information-line mr-3" style="font-size: 1.5rem;" v-if="isWaitingMode"></i>
                <i class="ri-error-warning-line mr-3" style="font-size: 1.5rem;" v-else></i>
                <div>
                    {{ isWaitingMode ? ($t('waiting_approval_documents_desc') || 'Öğrenci İşleri Onayı Bekleniyor') : $t('please_reupload_rejected_documents') }}
                </div>
            </b-alert>

            <fieldset class="border p-3 mb-4 rounded bg-white shadow-sm" v-if="!loadingParams">
                <legend class="w-auto px-2 font-weight-bold h6" :class="isWaitingMode ? 'text-info' : 'text-danger'">
                    {{ isWaitingMode ? ($t('uploaded_documents') || 'Yüklenen Belgeler') : $t('rejected_documents') }}
                </legend>
                <b-table-simple hover small responsive class="border rounded text-center">
                    <b-thead head-variant="light">
                        <b-tr>
                            <b-th class="text-left font-weight-bold">{{$t('document_type')}}</b-th>
                            <b-th class="font-weight-bold">{{ $t('status') }}</b-th>
                            <b-th class="font-weight-bold" v-if="!isWaitingMode">{{ $t('rejection_reason') }}</b-th>
                            <b-th class="font-weight-bold">{{$t('file')}}</b-th>
                        </b-tr>
                    </b-thead>
                    <b-tbody>
                        <b-tr v-for="(doc, index) in requiredDocuments" :key="index">
                            <b-td class="align-middle text-left font-weight-bold">
                                {{ getLocaleText(doc, 'document_type_name') }}
                                <div v-if="formData.documents[doc.document_type]" class="small mt-2 text-success font-weight-bold">
                                    <i class="ri-check-line"></i> {{ $t('was_elected') }}
                                </div>
                            </b-td>
                            <b-td class="align-middle">
                                <b-badge :variant="doc.status === 'rejected' ? 'danger' : (['uploaded','approved'].includes(doc.status) ? 'success' : 'secondary')">
                                    {{ doc.status === 'rejected' ? $t('rejected') : (['uploaded','approved'].includes(doc.status) ? $t('status_uploaded') : $t('status_waiting')) }}
                                </b-badge>
                            </b-td>
                            <b-td class="align-middle font-weight-bold" style="max-width: 250px;" v-if="!isWaitingMode">
                                {{ doc.description || '-' }}
                            </b-td>
                            <b-td class="align-middle text-center">
                                <div v-if="isWaitingMode" class="text-success font-weight-bold">
                                    <i class="ri-checkbox-circle-line"></i> {{ $t('status_uploaded') || 'Belge Yüklendi' }}
                                </div>
                                <ValidationProvider v-else :name="'files.'+index" rules="required" v-slot="{valid, errors}">
                                    <b-form-group :label="$t('file')">
                                        <div class="d-flex custom-file-upload">
                                            <b-form-file
                                                v-model="formData.documents[doc.document_type]"
                                                :state="errors[0] ? false : (valid ? true : null)"
                                                :placeholder="$t('select_file')"
                                                :drop-placeholder="$t('drop_file')"
                                                :accept="'.pdf,.jpg,.jpeg,.png'"
                                                :ref="'fileInput_' + index"
                                            ></b-form-file>
                                            <b-button variant="outline-secondary"
                                                      @click="$refs['fileInput_' + index][0].$el.childNodes[0].click();">
                                                {{ $t('browse') }}
                                            </b-button>
                                        </div>
                                        <div class="invalid-feedback d-block" v-if="errors[0]" v-html="errors[0]"></div>
                                    </b-form-group>
                                </ValidationProvider>
                            </b-td>
                        </b-tr>
                    </b-tbody>
                </b-table-simple>
            </fieldset>
            <div v-else class="text-center py-5">
                <b-spinner variant="primary"></b-spinner>
                <p class="mt-2 text-muted">{{$t('loading')}}</p>
            </div>
            <div class="d-flex justify-content-center mt-4" v-if="!isWaitingMode">
                <b-button variant="primary" @click="submitAndNext" class="px-5 font-weight-bold shadow-sm">
                    <i class="ri-save-line mr-1"></i> {{ $t('save') }}
                </b-button>
            </div>
        </ValidationObserver>
    </div>
</template>

<script>
import {ValidationObserver} from "vee-validate";
import SpecialStudentApplicationRegisterServices from "@/services/SpecialStudentApplicationRegisterServices";

export default {
    name: "RejectedDocumentsUpload",
    components: {
        ValidationObserver
    },
    props: {
        countDown: {
            type: Number,
            default: 0
        },
        isWaitingMode: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            loadingParams: false,
            formData: {
                documents: {}
            },
            requiredDocuments: []
        }
    },
    created() {
        this.setDocuments();
    },
    methods: {
        setDocuments() {
            this.loadingParams = true;
            this.formData = this.getWithExpiry('specialStudentData', true) || {};
            const documents = this.formData.documents ?? [];

            if (this.isWaitingMode) {
                this.requiredDocuments = documents;
            } else {
                this.requiredDocuments = documents.filter(doc => doc.status === 'rejected');
                const newObject = {};
                this.requiredDocuments.forEach((item) => {
                    newObject[item.document_type] = null;
                });
                this.formData.documents = newObject;
            }

            this.loadingParams = false;
        },
        async submitAndNext() {
            const isValid = await this.$refs.documentsForm.validate();
            if (!isValid) return;

            this.saveDocuments();
        },
        saveDocuments() {
            const formData = new FormData();
            if (this.formData.national_id) {
                formData.append('national_id', this.formData.national_id);
            }
            if (this.formData.passport_number) {
                formData.append('passport_number', this.formData.passport_number);
            }
            formData.append('pin', this.formData.pin);

            Object.entries(this.formData.documents).forEach(([key, value]) => {
                if (value === null) return;
                formData.append('files[]', value);
                formData.append('document_types[]', key);
            });

            SpecialStudentApplicationRegisterServices.uploadDocuments(formData).then(response => {
                this.setWithExpiry('specialStudentData', response.data.data, this.countDown, true);
                this.$toast.success(this.$t("proccess_success"));
                this.$emit('success');
            }).catch(e => {
                this.showErrors(e, this.$refs.documentsForm);
            });
        }
    }
}
</script>

<style scoped>
.custom-file-upload {
    gap: 0.5rem;
}
</style>
